/**
 * The categorising follow-up: a second, text-only call that files the lines of
 * a receipt already read, for when the category list did not fit in the
 * reading request next to the image.
 *
 * Categories are a suggestion, never part of the reading. So this call is
 * best-effort: when it fails, or does not fit either, the reading stands
 * without categories rather than failing with them. Only a caller's own abort
 * is passed on.
 */

import { describedCategories, type CategoryOption } from '../categories.js';
import { CATEGORY_JSON_SCHEMA, compactCategorySchema, lineKind } from '../extraction.js';
import { parseAmount } from '../parse.js';
import { filingFromLines, linesCategoryGrammar, linesCategorySystemPrompt, shortCategoryNames, type FilingLayout } from '../filing-layout.js';
import { buildCategorySystemPrompt, buildCategoryUserPrompt, buildCompactCategorySystemPrompt, type CategoryLine } from '../prompt.js';

import { ProviderError, type ProviderCallResult } from './core.js';
import { estimateInputTokens } from './budget.js';

/** What a follow-up request is: text only, no image. */
export interface CategoryRequest {
  system: string;
  userPrompt: string;
  schema: Record<string, unknown>;
  /**
   * A GBNF grammar to send in place of the schema (the lines layout, `filing-layout.ts`):
   * the answer then comes back as text, `raw.text`, not as JSON.
   */
  grammar?: string;
}

/** What the provider's send hands back for it. */
export type CategoryResponse = Pick<ProviderCallResult, 'raw' | 'inputTokens' | 'outputTokens'>;

export interface CategoryFollowUpOptions {
  categories?: readonly CategoryOption[] | null;
  /** From `extractionPromptsWithinBudget`: the reading request went without the list. */
  categoriesOmitted: boolean;
  /** The input budget the follow-up has to fit, as the reading request had. */
  inputTokens: number;
  /** Offer the list described (`describedCategories`), when that fits the budget. */
  describeCategories?: boolean;
  /** Ask for the compact answer (`compactCategorySchema`): a category per line and one for the receipt, nothing else. */
  compact?: boolean;
  /**
   * `lines`: the compact answer as numbered lines with short names, under a grammar
   * (`filing-layout.ts`) - only for the categorizer trained on it, behind a send that
   * passes `grammar` on. Implies `compact`.
   */
  layout?: FilingLayout;
  signal?: AbortSignal;
}

/**
 * The lines of a raw reading worth filing, numbered by their place in
 * `raw.items`: the product name and nothing else. Discount rows are left out -
 * a "Rabatt" line is not a purchase of anything - and so is every amount,
 * the VAT table and the payment: none of it says what was bought.
 */
export function linesToCategorize(raw: Record<string, unknown>): CategoryLine[] {
  const items = Array.isArray(raw.items) ? raw.items : [];
  // On a return every product is negative, so a minus sign is no discount.
  const total = parseAmount(raw.total);
  const returned = total !== null && total < 0;
  const lines: CategoryLine[] = [];
  for (const [index, item] of items.entries()) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const printed = typeof row.rawName === 'string' ? row.rawName : null;
    const name = typeof row.name === 'string' && row.name.trim() ? row.name.trim() : printed?.trim();
    if (!name) continue;
    if (lineKind(name, printed, parseAmount(row.totalPrice), returned).isDiscount) continue;
    lines.push({ line: index + 1, name });
  }
  return lines;
}

function merchantOf(raw: Record<string, unknown>): string | null {
  const merchant = raw.merchant;
  if (!merchant || typeof merchant !== 'object') return null;
  const name = (merchant as Record<string, unknown>).name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/**
 * The reading with the follow-up's categories written into it, as if the
 * model had given them the first time: `category` on the receipt, and
 * `category`, `categoryConfidence` and `alternativeCategories` on each item.
 * Answers for a line that does not exist are dropped.
 */
export function mergeCategoryAnswer(raw: Record<string, unknown>, answer: Record<string, unknown>): Record<string, unknown> {
  const items = Array.isArray(raw.items) ? raw.items.map((item: unknown) => item) : [];
  const answers = Array.isArray(answer.items) ? answer.items : [];
  for (const entry of answers) {
    if (!entry || typeof entry !== 'object') continue;
    const { line, category, categoryConfidence, alternativeCategories } = entry as Record<string, unknown>;
    const index = typeof line === 'number' ? line - 1 : typeof line === 'string' ? Number(line) - 1 : NaN;
    const item = Number.isInteger(index) ? items[index] : undefined;
    if (!item || typeof item !== 'object') continue;
    items[index] = {
      ...(item as Record<string, unknown>),
      category: category ?? null,
      categoryConfidence: categoryConfidence ?? null,
      alternativeCategories: Array.isArray(alternativeCategories) ? alternativeCategories : [],
    };
  }
  const others = Array.isArray(answer.otherCategories) ? answer.otherCategories : [];
  return { ...raw, items, category: answer.receiptCategory ?? null, otherCategories: others };
}

/**
 * A filing answer with every category it names passed through `toKey`: the
 * receipt's, its other ones, and each line's and its alternatives'.
 */
export function filingAnswerKeys(answer: Record<string, unknown>, toKey: (answer: unknown) => unknown): Record<string, unknown> {
  const alternative = (entry: unknown): unknown => {
    if (!entry || typeof entry !== 'object') return toKey(entry);
    const row = { ...(entry as Record<string, unknown>) };
    if ('key' in row) row.key = toKey(row.key);
    if ('category' in row) row.category = toKey(row.category);
    return row;
  };
  const line = (item: unknown): unknown => {
    if (!item || typeof item !== 'object') return item;
    const row = { ...(item as Record<string, unknown>) };
    row.category = toKey(row.category);
    if (Array.isArray(row.alternativeCategories)) row.alternativeCategories = row.alternativeCategories.map(alternative);
    return row;
  };
  return {
    ...answer,
    receiptCategory: toKey(answer.receiptCategory),
    otherCategories: Array.isArray(answer.otherCategories) ? answer.otherCategories.map(toKey) : answer.otherCategories,
    items: Array.isArray(answer.items) ? answer.items.map(line) : answer.items,
  };
}

/**
 * A compact filing answer (`compactCategorySchema`) in the full answer's
 * shape, so everything after reads it as before: no confidences, no
 * alternatives, no other receipt categories. An answer already in the full
 * shape, from a model that ignored the format, is taken as it is.
 */
export function filingFromCompact(answer: Record<string, unknown>): Record<string, unknown> {
  if (!('lines' in answer) && Array.isArray(answer.items)) return answer;
  const lines = answer.lines && typeof answer.lines === 'object' ? (answer.lines as Record<string, unknown>) : {};
  return {
    receiptCategory: answer.receipt ?? null,
    otherCategories: [],
    items: Object.entries(lines)
      .filter(([line]) => /^\d+$/.test(line))
      .map(([line, category]) => ({ line: Number(line), category: typeof category === 'string' ? category : null })),
  };
}

/**
 * Runs the follow-up when the reading went without the category list, and
 * folds its answer into the reading. Otherwise the reading is returned as is.
 *
 * `categoriesOmitted` on the result is true only when the model never got to
 * see the list at all: the follow-up did not fit, or failed.
 */
export async function followUpCategories<T extends ProviderCallResult>(
  reading: T,
  options: CategoryFollowUpOptions,
  send: (request: CategoryRequest) => Promise<CategoryResponse>,
): Promise<T> {
  const categories = options.categories ?? [];
  if (!options.categoriesOmitted || categories.length === 0) return reading;

  const lines = linesToCategorize(reading.raw);
  if (lines.length === 0 && !merchantOf(reading.raw)) return { ...reading, categoriesOmitted: true };

  const userPrompt = buildCategoryUserPrompt(merchantOf(reading.raw), lines);
  const fits = (request: CategoryRequest) =>
    estimateInputTokens(request.system, request.userPrompt, 0, request.schema) <= options.inputTokens;
  const numbers = lines.map((entry) => entry.line);
  const shortNames = new Map<CategoryRequest, Map<string, string>>();
  const build = (offered: readonly CategoryOption[], notes = ''): CategoryRequest => {
    if (options.layout === 'lines') {
      const short = shortCategoryNames(offered);
      const request = {
        system: linesCategorySystemPrompt(buildCompactCategorySystemPrompt(offered, notes)),
        userPrompt,
        schema: {},
        grammar: linesCategoryGrammar([...short.values()], numbers),
      };
      shortNames.set(request, short);
      return request;
    }
    return options.compact
      ? { system: buildCompactCategorySystemPrompt(offered, notes), userPrompt, schema: compactCategorySchema(offered, numbers) }
      : { system: buildCategorySystemPrompt(offered, notes), userPrompt, schema: CATEGORY_JSON_SCHEMA };
  };
  // Described when it fits, plain when only that does: the notes are worth less than the list.
  const described = options.describeCategories ? describedCategories(categories) : null;
  const describedRequest = described && build(described.categories, described.notes);
  const request = describedRequest && fits(describedRequest) ? describedRequest : build(categories);
  const toKey = request === describedRequest ? described?.toKey : undefined;
  if (!fits(request)) return { ...reading, categoriesOmitted: true };

  let answer: CategoryResponse;
  try {
    answer = await send(request);
  } catch (error) {
    if (options.signal?.aborted || (error instanceof ProviderError && error.kind === 'aborted')) throw error;
    return { ...reading, categoriesOmitted: true };
  }

  const filingIn = (raw: Record<string, unknown>, back: typeof toKey) => (back ? filingAnswerKeys(raw, back) : raw);
  const short = shortNames.get(request);
  const filingOf = (raw: Record<string, unknown>) =>
    short ? filingFromLines(typeof raw.text === 'string' ? raw.text : '', short) : options.compact ? filingFromCompact(raw) : raw;
  const add = (a: number | null, b: number | null): number | null => (a === null && b === null ? null : (a ?? 0) + (b ?? 0));
  return {
    ...reading,
    raw: mergeCategoryAnswer(reading.raw, filingIn(filingOf(answer.raw), toKey)),
    inputTokens: add(reading.inputTokens, answer.inputTokens),
    outputTokens: add(reading.outputTokens, answer.outputTokens),
    categoriesOmitted: false,
    passes: (reading.passes ?? 1) + 1,
  };
}
